import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, Row, Col, Label, Input } from 'reactstrap';
import CustomSelect from '../../Base/Common/CustomSelect';
import TextInspector from './inspector/TextInspector';
import ImageInspector from './inspector/ImageInspector';
import QrCodeInspector from './inspector/QrCodeInspector';
import BarcodeInspector from './inspector/BarcodeInspector';
import ShapeInspector from './inspector/ShapeInspector';
import HotelImagePicker from './inspector/HotelImagePicker';
import { VISIBILITY_OPTIONS } from './templateUtils';

class PropertyInspector extends Component {
    _updateField = (field, value) => {
        const { element, onChange } = this.props;
        onChange({ ...element, [field]: value });
    };

    _renderTypeInspector = () => {
        const { element, onChange } = this.props;
        switch (element.type) {
            case 'text':    return <TextInspector element={element} onChange={onChange} />;
            case 'image':   return <ImageInspector element={element} onChange={onChange} />;
            case 'qr':      return <QrCodeInspector element={element} onChange={onChange} />;
            case 'barcode': return <BarcodeInspector element={element} onChange={onChange} />;
            case 'shape':   return <ShapeInspector element={element} onChange={onChange} />;
            default:        return null;
        }
    };

    render() {
        const { element, onDelete } = this.props;

        if (!element) {
            const {
                template,
                onPageSizeChange,
                onOrientationChange,
                onBgColourChange,
                onBgImageChange,
                onBgImageOpacityChange,
                onPageMarginXChange,
                onPageMarginYChange,
            } = this.props;

            const pageSizeOptions = [
                { value: 'A4', label: 'A4' },
                { value: 'A5', label: 'A5' },
            ];

            return (
                <div style={{ padding: '12px', overflowY: 'auto', overflowX: 'hidden', height: '100%' }}>
                    <div className="mb-2" style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#6c757d', letterSpacing: '0.05em' }}>
                        <FormattedMessage id="TemplateDesigner.Inspector.PageProperties" />
                    </div>

                    {/* Page size */}
                    <div className="mb-3">
                        <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                            <FormattedMessage id="TemplateDesigner.Toolbar.PageSize" />
                        </Label>
                        <CustomSelect
                            options={pageSizeOptions}
                            value={pageSizeOptions.find(o => o.value === (template && template.pageSize)) || pageSizeOptions[0]}
                            onChange={(opt) => onPageSizeChange(opt ? opt.value : 'A4')}
                        />
                    </div>

                    {/* Orientation */}
                    <div className="mb-3">
                        <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                            <FormattedMessage id="TemplateDesigner.Toolbar.Orientation" />
                        </Label>
                        <div style={{ display: 'flex', gap: 4 }}>
                            <Button
                                size="sm"
                                color={template && template.orientation === 'portrait' ? 'primary' : 'secondary'}
                                onClick={() => onOrientationChange('portrait')}
                            >
                                <i className="fa-solid fa-mobile-alt mr-1" />
                                <FormattedMessage id="TemplateDesigner.Inspector.Portrait" />
                            </Button>
                            <Button
                                size="sm"
                                color={template && template.orientation === 'landscape' ? 'primary' : 'secondary'}
                                onClick={() => onOrientationChange('landscape')}
                            >
                                <i className="fa-solid fa-tablet-alt fa-rotate-90 mr-1" />
                                <FormattedMessage id="TemplateDesigner.Inspector.Landscape" />
                            </Button>
                        </div>
                    </div>

                    {/* Page margins */}
                    <div className="mb-3">
                        <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                            <FormattedMessage id="TemplateDesigner.Toolbar.Margin" />
                        </Label>
                        <Row className="no-gutters" style={{ gap: '4px' }}>
                            <Col>
                                <Label style={{ fontSize: '0.7rem', marginBottom: 2 }}>
                                    <FormattedMessage id="TemplateDesigner.Toolbar.MarginX" />
                                </Label>
                                <Input
                                    type="number"
                                    bsSize="sm"
                                    min={0}
                                    max={50}
                                    value={template ? template.pageMarginX || 0 : 0}
                                    onChange={(e) => onPageMarginXChange(parseFloat(e.target.value) || 0)}
                                />
                            </Col>
                            <Col>
                                <Label style={{ fontSize: '0.7rem', marginBottom: 2 }}>
                                    <FormattedMessage id="TemplateDesigner.Toolbar.MarginY" />
                                </Label>
                                <Input
                                    type="number"
                                    bsSize="sm"
                                    min={0}
                                    max={50}
                                    value={template ? template.pageMarginY || 0 : 0}
                                    onChange={(e) => onPageMarginYChange(parseFloat(e.target.value) || 0)}
                                />
                            </Col>
                        </Row>
                    </div>

                    <hr className="my-2" />

                    {/* Background colour */}
                    <div className="mb-3">
                        <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                            <FormattedMessage id="TemplateDesigner.Toolbar.BgColour" />
                        </Label>
                        <Input
                            type="color"
                            value={template ? template.bgColour || '#ffffff' : '#ffffff'}
                            onChange={(e) => onBgColourChange(e.target.value)}
                            style={{ width: 36, height: 30, padding: '2px 3px', cursor: 'pointer', display: 'block' }}
                        />
                    </div>

                    {/* Background image URL */}
                    <div className="mb-3">
                        <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                            <FormattedMessage id="TemplateDesigner.Toolbar.BgImage" />
                        </Label>
                        <div style={{ display: 'flex', gap: 4 }}>
                            <FormattedMessage id="TemplateDesigner.Toolbar.BgImagePlaceholder">
                                {placeholder => (
                                    <Input
                                        type="text"
                                        bsSize="sm"
                                        value={template ? template.bgImage || '' : ''}
                                        placeholder={placeholder}
                                        onChange={(e) => onBgImageChange(e.target.value)}
                                    />
                                )}
                            </FormattedMessage>
                            {template && template.bgImage && (
                                <Button size="sm" color="secondary" onClick={() => onBgImageChange('')} style={{ padding: '2px 6px' }}>
                                    ×
                                </Button>
                            )}
                        </div>
                        <div className="mt-1">
                            <HotelImagePicker onSelect={(url) => onBgImageChange(url)} />
                        </div>
                        {template && template.bgImage && (
                            <div className="mt-2">
                                <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                                    <FormattedMessage id="TemplateDesigner.Toolbar.BgImageOpacity" />
                                </Label>
                                <Input
                                    type="number"
                                    bsSize="sm"
                                    min={0}
                                    max={100}
                                    value={template.bgImageOpacity ?? 100}
                                    onChange={(e) => onBgImageOpacityChange(parseInt(e.target.value, 10) || 0)}
                                />
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        return (
            // overflowX hidden: reactstrap Rows carry -15px margins → phantom h-scrollbar
            <div style={{ padding: '12px', overflowY: 'auto', overflowX: 'hidden', height: '100%' }}>
                {/* Type label */}
                <div className="mb-2" style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#6c757d', letterSpacing: '0.05em' }}>
                    {element.type}
                </div>

                {/* Position & size */}
                <div className="mb-3">
                    <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                        <FormattedMessage id="TemplateDesigner.Inspector.Position" />
                    </Label>
                    <Row className="no-gutters" style={{ gap: '4px' }}>
                        <Col>
                            <Label style={{ fontSize: '0.7rem', marginBottom: 2 }}>X</Label>
                            <Input
                                type="number"
                                bsSize="sm"
                                value={element.x}
                                step={0.5}
                                onChange={(e) => this._updateField('x', parseFloat(e.target.value) || 0)}
                            />
                        </Col>
                        <Col>
                            <Label style={{ fontSize: '0.7rem', marginBottom: 2 }}>Y</Label>
                            <Input
                                type="number"
                                bsSize="sm"
                                value={element.y}
                                step={0.5}
                                onChange={(e) => this._updateField('y', parseFloat(e.target.value) || 0)}
                            />
                        </Col>
                    </Row>
                </div>

                <div className="mb-3">
                    <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                        <FormattedMessage id="TemplateDesigner.Inspector.Size" />
                    </Label>
                    <Row className="no-gutters" style={{ gap: '4px' }}>
                        <Col>
                            <Label style={{ fontSize: '0.7rem', marginBottom: 2 }}>W</Label>
                            <Input
                                type="number"
                                bsSize="sm"
                                value={element.w}
                                step={0.5}
                                min={1}
                                onChange={(e) => this._updateField('w', parseFloat(e.target.value) || 1)}
                            />
                        </Col>
                        <Col>
                            <Label style={{ fontSize: '0.7rem', marginBottom: 2 }}>H</Label>
                            <Input
                                type="number"
                                bsSize="sm"
                                value={element.h}
                                step={0.5}
                                min={1}
                                onChange={(e) => this._updateField('h', parseFloat(e.target.value) || 1)}
                            />
                        </Col>
                    </Row>
                </div>

                {/* z-index + rotation */}
                <div className="mb-3">
                    <Row className="no-gutters" style={{ gap: '4px' }}>
                        <Col>
                            <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                                <FormattedMessage id="TemplateDesigner.Inspector.LayerOrder" />
                            </Label>
                            <Input
                                type="number"
                                bsSize="sm"
                                value={element.zIndex || 1}
                                min={1}
                                max={100}
                                onChange={(e) => this._updateField('zIndex', parseInt(e.target.value, 10) || 1)}
                            />
                        </Col>
                        <Col>
                            <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                                <FormattedMessage id="TemplateDesigner.Inspector.Rotation" />
                            </Label>
                            <Input
                                type="number"
                                bsSize="sm"
                                value={element.rotation ?? 0}
                                min={-180}
                                max={180}
                                onChange={(e) => this._updateField('rotation', Math.max(-180, Math.min(180, parseInt(e.target.value, 10) || 0)))}
                            />
                        </Col>
                    </Row>
                </div>

                {/* Show condition: applies to the whole selection (e.g. every element of a gift-only block) */}
                <div className="mb-3">
                    <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                        <FormattedMessage id={this.props.multiSelection ? 'TemplateDesigner.Inspector.VisibilitySelection' : 'TemplateDesigner.Inspector.Visibility'} />
                    </Label>
                    <Input
                        type="select"
                        bsSize="sm"
                        value={element.visibleWhen || ''}
                        onChange={(e) => this.props.onVisibilityChange(e.target.value)}
                    >
                        {VISIBILITY_OPTIONS.map(o => (
                            <FormattedMessage key={o.value} id={o.labelKey}>
                                {label => <option value={o.value}>{label}</option>}
                            </FormattedMessage>
                        ))}
                    </Input>
                </div>

                {/* Alignment: multi gets two rows (selection + page); single gets page only */}
                {(() => {
                    const ALIGN_MODES = [
                        { mode: 'left',    icon: 'fa-solid fa-align-left',   rotate: false, title: 'Align left' },
                        { mode: 'centerH', icon: 'fa-solid fa-align-center', rotate: false, title: 'Align centre' },
                        { mode: 'right',   icon: 'fa-solid fa-align-right',  rotate: false, title: 'Align right' },
                        { mode: 'top',     icon: 'fa-solid fa-align-left',   rotate: true,  title: 'Align top' },
                        { mode: 'middleV', icon: 'fa-solid fa-align-center', rotate: true,  title: 'Align middle' },
                        { mode: 'bottom',  icon: 'fa-solid fa-align-right',  rotate: true,  title: 'Align bottom' },
                    ];
                    const renderRow = (labelId, target) => (
                        <div className="mb-2">
                            <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                                <FormattedMessage id={labelId} />
                            </Label>
                            <div className="d-flex" style={{ gap: 4 }}>
                                {ALIGN_MODES.map(({ mode, icon, rotate, title }) => (
                                    <Button
                                        key={target + mode}
                                        size="sm"
                                        color="secondary"
                                        title={title}
                                        style={{ flex: 1, padding: '4px 0' }}
                                        onClick={() => this.props.onAlign(mode, target)}
                                    >
                                        <i className={icon} style={rotate ? { transform: 'rotate(90deg)' } : undefined} />
                                    </Button>
                                ))}
                            </div>
                        </div>
                    );
                    return (
                        <div className="mb-2">
                            {this.props.multiSelection && renderRow('TemplateDesigner.Inspector.AlignSelection', 'selection')}
                            {renderRow('TemplateDesigner.Inspector.AlignPage', 'page')}
                        </div>
                    );
                })()}

                <hr className="my-2" />

                {/* Type-specific inspector */}
                {this._renderTypeInspector()}

                <hr className="my-2" />

                {/* Duplicate + Delete — icon-only, side by side (labels via tooltip) */}
                <div className="d-flex" style={{ gap: 8 }}>
                    <FormattedMessage id="TemplateDesigner.Inspector.Duplicate">
                        {title => (
                            <Button
                                color="secondary"
                                size="sm"
                                title={title}
                                style={{ flex: 1 }}
                                onClick={() => this.props.onDuplicate(element.id)}
                            >
                                <i className="fa-regular fa-copy" />
                            </Button>
                        )}
                    </FormattedMessage>
                    <FormattedMessage id="TemplateDesigner.Inspector.Delete">
                        {title => (
                            <Button
                                color="danger"
                                size="sm"
                                title={title}
                                style={{ flex: 1 }}
                                disabled={!!element.locked}
                                onClick={() => onDelete(element.id)}
                            >
                                <i className={element.locked ? 'fa-solid fa-lock' : 'fa-solid fa-trash-can'} />
                            </Button>
                        )}
                    </FormattedMessage>
                </div>
            </div>
        );
    }
}

export default PropertyInspector;
